import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { environment } from '@environments/environment';
import { getMessaging, getToken } from '@angular/fire/messaging';

import { MessagingService } from '@app/services/messaging.service';
import { NotificationInboxService } from '@app/services/notification-inbox.service';
import {
  MatDrawerToggleResult,
  MatSidenav,
  MatSidenavModule,
} from '@angular/material/sidenav';
import { ProjectsService } from '@app/services/project.service';
import { CompanyService } from '@app/services/company.service';

import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatMenuModule } from '@angular/material/menu';
import { MatBadgeModule } from '@angular/material/badge';
import { SideMenuComponent } from '../side-menu/side-menu.component';
import { NotificationListComponent } from '@app/notification/notification-list/notification-list.component';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { LayoutService } from '../layout.service';

@Component({
  selector: 'app-authorized-layout',
  templateUrl: './authorized-layout.component.html',
  styleUrls: ['./authorized-layout.component.scss'],
  providers: [ProjectsService, CompanyService],
  imports: [
    AsyncPipe,
    RouterModule,
    MatSidenavModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MatMenuModule,
    MatBadgeModule,
    SideMenuComponent,
    NotificationListComponent,
  ],
})
export class AuthorizedLayoutComponent implements OnInit, OnDestroy {
  mobileMenuIsOpen = false;
  @ViewChild('mobileMenu') mobileMenu!: MatSidenav;
  private destroy$ = new Subject<void>();

  readonly unreadCount$ = this.notificationInboxService.unreadCount$;
  readonly companyName$ = this.layoutService.companyName$;
  readonly projectTitle$ = this.layoutService.projectTitle$;

  constructor(
    private messagingService: MessagingService,
    private notificationInboxService: NotificationInboxService,
    public auth: AngularFireAuth,
    private layoutService: LayoutService,
    private router: Router,
  ) {}

  toggleMobileMenu() {
    if (this.mobileMenuIsOpen) {
      this.closeMobileMenu();
    } else {
      this.openMobileMenu();
    }
  }

  openMobileMenu() {
    this.mobileMenu
      .open()
      .then((res: MatDrawerToggleResult) => {
        this.mobileMenuIsOpen = true;
      })
      .catch((err) => {
        console.error('Error occurred while opening mobile menu.', err);
      });
  }

  closeMobileMenu() {
    this.mobileMenu
      .close()
      .then((res: MatDrawerToggleResult) => {
        this.mobileMenuIsOpen = false;
      })
      .catch((err) => {
        console.error('Error occurred while closing mobile menu.', err);
      });
  }

  clickOnVoice() {
    this.closeMobileMenu();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    // Populate the notification inbox immediately. Done up-front (not gated
    // behind the messagingService.requestPermission flow below) so the badge
    // counter and notifications panel render the real state even for users
    // who deny or never granted push permission.
    this.notificationInboxService.load();

    // GA4: set user ID once authenticated
    this.auth.user.pipe(
      filter((user) => !!user),
      takeUntil(this.destroy$),
    ).subscribe((user) => {
      if (typeof gtag === 'function') {
        gtag('set', { user_id: user!.uid });
      }
    });

    // GA4: track SPA page navigation
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      takeUntil(this.destroy$),
    ).subscribe((event) => {
      if (this.mobileMenuIsOpen) {
        this.closeMobileMenu();
      }
      if (typeof gtag === 'function') {
        gtag('event', 'page_view', {
          page_path: (event as NavigationEnd).urlAfterRedirects,
        });
      }
    });

    let requestPermissionSubject = this.messagingService.requestPermission();
    requestPermissionSubject.subscribe({
      next: (permission) => {
        this.messagingService.requestForToken().then((token) => {
          this.messagingService
            .createDeviceToken({}, { token: token })
            .subscribe({
              next: () => {
                this.notificationInboxService.load();
              },
              error: (error) => {
                console.error('Error creating Device Token.', error);
                this.notificationInboxService.load();
              },
            });
        });
      },
      error: (error) => {
        console.error('Error requesting permission.', error);
        this.notificationInboxService.load();
      },
    });
  }

  getMessagingToken() {
    const messaging = getMessaging();
    getToken(messaging, { vapidKey: environment.firebase.vapidKey })
      .then((currentToken) => {
        if (currentToken) {
          console.log('Got FCM token:', currentToken);
        }
      })
      .catch((err) => {
        console.log('An error occurred while retrieving token. ', err);
      });
  }
}
